import { View } from "@novorender/api";
import { mat3, quat, vec2, vec3 } from "gl-matrix";
import { useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { getCameraDir } from "features/engine2D/utils";
import { RenderState, selectCameraDefaults } from "features/render";
import { degToRad } from "utils/math";

// How far in pixels can the knob visually deviate from center
export const VISUAL_RADIUS = 20;

// How far should we move the knob in pixels to set value to 1
export const CONTROL_RADIUS = 60;

export const MOVE_Z_SPEED = 0.25;
export const MOVE_SPEED = 0.25;
export const ROTATE_SPEED = 0.00666;

export type Orientation = {
    alpha: number;
    beta: number;
    gamma: number;
};

// All values (moveX, etc.) are from -1 to 1
export type ControlState = {
    // Move camera up/down vertically
    moveZ: number;
    moveZPointerId: number | null;
    moveZStartPos: vec2;
    // Move camera left/right and forward/backward on XY plane
    moveX: number;
    moveY: number;
    movePointerId: number | null;
    moveStartPos: vec2;
    // Rotate camera left/right and up/down
    rotateX: number;
    rotateY: number;
    rotatePointerId: number | null;
    rotateStartPos: vec2;
    // Rotation in orientation mode
    orientationInitialRotation: quat;
    initialOrientation: Orientation | null;
    orientation: Orientation | null;
};

export const DEFAULT_CONTROL_STATE: ControlState = {
    moveZ: 0,
    moveZPointerId: null,
    moveZStartPos: vec2.create(),
    moveX: 0,
    moveY: 0,
    movePointerId: null,
    moveStartPos: vec2.create(),
    rotateX: 0,
    rotateY: 0,
    rotatePointerId: null,
    rotateStartPos: vec2.create(),
    orientationInitialRotation: quat.create(),
    initialOrientation: null,
    orientation: null,
};

export function useUpdateCamera(state: ControlState) {
    const {
        state: { view },
    } = useExplorerGlobals();
    const defaults = useAppSelector(selectCameraDefaults);
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        if (!view) {
            return;
        }

        let mounted = true;
        let raf: number | null = null;

        const callback = () => {
            updateCamera(view, defaults.pinhole.controller, stateRef.current, screen.orientation.type);

            if (mounted) {
                raf = requestAnimationFrame(callback);
            }
        };

        raf = requestAnimationFrame(callback);

        return () => {
            mounted = false;
            if (raf !== null) {
                cancelAnimationFrame(raf);
            }
        };
    }, [defaults.pinhole.controller, view]);
}

export function updateCamera(
    view: View,
    controllerType: RenderState["cameraDefaults"]["pinhole"]["controller"],
    state: ControlState,
    orientationType: OrientationType,
) {
    let cameraPos = view.renderState.camera.position;
    let cameraRot = view.renderState.camera.rotation;

    const m = mat3.fromQuat(mat3.create(), view.renderState.camera.rotation);
    const forward = vec3.fromValues(m[6], m[7], m[8]);
    const right = vec3.fromValues(m[0], m[1], m[2]);

    if (state.moveZ !== 0) {
        const mvZ = state.moveZ * MOVE_Z_SPEED;
        // [BM-9869] - use power curve because customers are used to it
        const pow = Math.pow(mvZ, 2) * Math.sign(mvZ);
        const offset = vec3.fromValues(0, 0, -pow);
        cameraPos = vec3.add(vec3.create(), cameraPos, offset);
    }

    if (state.moveX !== 0 || state.moveY !== 0) {
        const mvX = state.moveX * MOVE_SPEED;
        const mvY = state.moveY * MOVE_SPEED;
        // [BM-9869] - use power curve because customers are used to it
        const powX = Math.pow(mvX, 2) * Math.sign(mvX);
        const powY = Math.pow(mvY, 2) * Math.sign(mvY);
        const flatRight = vec3.clone(right);
        flatRight[2] = 0;
        vec3.normalize(flatRight, flatRight);
        const flatForward = vec3.clone(forward);
        flatForward[2] = 0;
        vec3.normalize(flatForward, flatForward);
        const xOffset = vec3.scale(vec3.create(), flatRight, powX);
        const yOffset = vec3.scale(vec3.create(), flatForward, powY);
        cameraPos = vec3.clone(cameraPos);
        vec3.add(cameraPos, cameraPos, xOffset);
        vec3.add(cameraPos, cameraPos, yOffset);
    }

    if (state.orientation && state.initialOrientation) {
        // NOTE: Handling this stuff seems to be a bit more complex than it should be -
        //       I expect all the different orientations could be handled with general quat operations,
        //       but I couldn't figure them out, so there are a couple of tweaks instead.
        // Relative rotation must be computed between quaternions, not between orientations.
        //
        // Why pass alpha = 0 here and pass relative alpha for q2?
        // For some reason on Android (my pixel 9 at least) and on iPhone XS initial alpha are different.
        // On iPhone it's close to 0, on Android it's close to 270.
        // For some reason this initial 270 degree breaks the rotation (I don't fully understand why).
        // Adjusting it by 90 degrees breaks iPhone though.
        // So just treat original alpha as 0 and then use relative alpha for q2.
        const initGammaSign = state.initialOrientation.gamma > 0 ? -1 : 1;
        const q1 = deviceOrientationToQuat(
            0,
            state.initialOrientation.beta,
            state.initialOrientation.gamma,
            orientationType,
            initGammaSign,
        );
        const q1Inv = quat.invert(quat.create(), q1);
        const q2 = deviceOrientationToQuat(
            state.orientation.alpha - state.initialOrientation.alpha,
            state.orientation.beta,
            state.orientation.gamma,
            orientationType,
            initGammaSign,
        );
        // Rotation of q2 relative to q1 is q2 * q1'
        const relRotation = quat.mul(quat.create(), q2, q1Inv);
        const newCameraRot = quat.mul(quat.create(), state.orientationInitialRotation, relRotation);
        if (!quat.equals(newCameraRot, cameraRot)) {
            cameraRot = newCameraRot;
        }
    } else if (state.rotateX !== 0 || state.rotateY !== 0) {
        cameraRot = quat.clone(cameraRot);
        quat.rotateY(cameraRot, cameraRot, ROTATE_SPEED * -state.rotateX);

        // Prevent camera flipping like crazy when it passes looking directly up or down
        // by stopping the rotation when it passes the threshold
        const yOffset = -ROTATE_SPEED * state.rotateY;
        const dotUp = vec3.dot(getCameraDir(cameraRot), vec3.fromValues(0, 0, 1));
        if ((yOffset < 0 && dotUp > -0.999) || (yOffset > 0 && dotUp < 0.999)) {
            quat.rotateX(cameraRot, cameraRot, yOffset);
        }
    }

    if (cameraPos !== view.renderState.camera.position || cameraRot !== view.renderState.camera.rotation) {
        const controller = view.controllers[controllerType];
        controller.moveTo(cameraPos, 0, cameraRot);
    }
}

export function handlePointerMove(state: ControlState, event: PointerEvent): ControlState {
    const getKnobPos = (startPos: vec2, canMoveX: boolean): vec2 => {
        const delta = vec2.sub(vec2.create(), pointerPos, startPos);
        if (!canMoveX) {
            delta[0] = 0;
        }
        const distance = vec2.length(delta);
        if (distance === 0) {
            return [0, 0];
        }
        const controlValue = distance / CONTROL_RADIUS;
        vec2.normalize(delta, delta);
        return [delta[0] * controlValue, delta[1] * controlValue];
    };

    const pointerPos = vec2.fromValues(event.clientX, event.clientY);

    switch (event.pointerId) {
        case state.movePointerId: {
            const [x, y] = getKnobPos(state.moveStartPos, true);
            return {
                ...state,
                moveX: x,
                moveY: y,
            };
        }

        case state.moveZPointerId: {
            const [, y] = getKnobPos(state.moveZStartPos, false);
            return {
                ...state,
                moveZ: y,
            };
        }

        case state.rotatePointerId: {
            const [x, y] = getKnobPos(state.rotateStartPos, true);
            return {
                ...state,
                rotateX: x,
                rotateY: y,
            };
        }
    }

    return state;
}

export function handlePointerEnd(state: ControlState, event: PointerEvent): ControlState {
    switch (event.pointerId) {
        case state.moveZPointerId: {
            return {
                ...state,
                moveZPointerId: null,
                moveZ: 0,
            };
        }

        case state.movePointerId: {
            return {
                ...state,
                movePointerId: null,
                moveX: 0,
                moveY: 0,
            };
        }

        case state.rotatePointerId: {
            return {
                ...state,
                rotatePointerId: null,
                rotateX: 0,
                rotateY: 0,
            };
        }
    }

    return state;
}

// Build device quaternion from alpha/beta/gamma (degrees)
export function deviceOrientationToQuat(
    alphaDeg: number,
    betaDeg: number,
    gammaDeg: number,
    orientationType: OrientationType,
    initGammaSign: number,
) {
    const alpha = degToRad(alphaDeg); // Z
    const beta = degToRad(betaDeg); // X'
    const gamma = degToRad(gammaDeg); // Y''

    // --- Z-X'-Y'' intrinsic is commonly implemented via an equivalent Euler in order 'YXZ'
    // three.js does: euler.set(beta, alpha, -gamma, 'YXZ'); then:

    // gl-matrix doesn't have arbitrary-order Euler, so we compose quats explicitly
    const qAlpha = quat.setAxisAngle(quat.create(), vec3.fromValues(0, 1, 0), alpha);
    let qBeta = quat.create();
    let qGamma = quat.create();

    switch (orientationType) {
        case "landscape-primary":
        case "landscape-secondary":
            // Why multiply gamma by initGammaSign?
            // In portrait mode rotation direction up and down depends on initial gamma sign,
            // so if you enter orientation mode holding device against the floor and against the ceiling -
            // you get different rotation direction and it's compensated by initGammaSign.
            qBeta = quat.setAxisAngle(quat.create(), vec3.fromValues(0, 0, 1), beta);
            qGamma = quat.setAxisAngle(quat.create(), vec3.fromValues(1, 0, 0), -gamma * initGammaSign);
            break;
        case "portrait-primary":
            quat.setAxisAngle(qBeta, vec3.fromValues(1, 0, 0), beta);
            quat.setAxisAngle(qGamma, vec3.fromValues(0, 0, 1), -gamma);
            break;
        case "portrait-secondary":
            quat.setAxisAngle(qBeta, vec3.fromValues(1, 0, 0), -beta);
            quat.setAxisAngle(qGamma, vec3.fromValues(0, 0, 1), gamma);
            break;
    }

    // intrinsic Z then X' then Y''  => multiply in that conceptual order
    const q = quat.mul(quat.create(), qAlpha, qBeta);
    quat.mul(q, q, qGamma);

    return q;
}
